import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { SearchSection, SearchSectionSpacer } from '@vs-shop/ui/searchCombobox';
import { Skeleton } from '@vs-shop/ui/skeleton';
import { clsxm } from '@vs-shop/utils';

import { searchSuggestProductGridClasses } from './searchSuggest.tsx';

/**
 * The panel a search field drops open: the columns it is laid out in, the line
 * it shows when there is nothing to show, and the shapes it holds while it
 * waits.
 *
 *   <SearchComboboxList>
 *     <SearchPanelColumn variant={SearchPanelColumnVariant.GROUPED} className="lap:col-span-1">
 *       {linkGroups}
 *     </SearchPanelColumn>
 *     <SearchPanelColumn variant={SearchPanelColumnVariant.FLUSH} className="lap:col-span-1">
 *       {topProduct}
 *     </SearchPanelColumn>
 *     <SearchPanelColumn variant={SearchPanelColumnVariant.FLUSH} className="lap:col-span-2">
 *       {products}
 *       <SearchPanelActions>{allResultsCta}</SearchPanelActions>
 *     </SearchPanelColumn>
 *   </SearchComboboxList>
 *
 * **The field itself is not here.** `@vs-shop/ui/searchCombobox` already ships
 * the combobox, its form, its popup and its grid, and every part in this
 * section composes into that grid rather than replacing any of it.
 *
 * **Where a column sits is the composition's; how it stacks inside itself is
 * the column's.** The grid span rides `className`, because which of four
 * tracks a column takes is layout between parts. What the appearance decides
 * is how far apart the things stacked inside one column stand.
 */

/**
 * A column of the panel.
 *
 * Renders the run inside it and the rule after it, both of which a composition
 * omitting them would still render — as a column with no separator and a stack
 * with no width floor, which is invisible until a long product name blows the
 * grid open.
 */
const searchPanelColumnClasses = 'lap:flex-row flex flex-col';

/** How the column's own contents stack. */
export const searchPanelColumnBodyVariants = cva('flex min-w-0 flex-1 flex-col', {
  variants: {
    variant: {
      /** Nothing between them — one thing in the column, or things that set their own spacing. */
      flush: '',
      /** Groups reading as separate lists. */
      grouped: 'gap-4',
      /** The widest setting, for placeholder shapes that need to read as rows of a page. */
      airy: 'gap-8',
    },
  },
});

type SearchPanelColumnVariantName = NonNullable<
  VariantProps<typeof searchPanelColumnBodyVariants>['variant']
>;

export type SearchPanelColumnVariant = SearchPanelColumnVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const SearchPanelColumnVariant = {
  /** Nothing between them. */
  FLUSH: 'flush',
  /** Groups reading as separate lists. */
  GROUPED: 'grouped',
  /** The widest setting. */
  AIRY: 'airy',
} as const satisfies Record<string, SearchPanelColumnVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type SearchPanelColumnVariantProps = { readonly variant: SearchPanelColumnVariant };

export type SearchPanelColumnProps = ComponentProps<'div'> & SearchPanelColumnVariantProps;

/** One column of the panel. */
export const SearchPanelColumn = ({
  className,
  variant,
  children,
  ...props
}: SearchPanelColumnProps) => (
  <div
    data-slot="search-panel-column"
    className={clsxm(searchPanelColumnClasses, className)}
    {...props}
  >
    <div
      data-slot="search-panel-column-body"
      className={searchPanelColumnBodyVariants({ variant })}
    >
      {children}
    </div>
    <SearchSectionSpacer />
  </div>
);

/**
 * The line the panel shows instead of results — nothing matched, or the
 * request failed. Centred in whatever the composition spans it across, because
 * a sentence in the leading column of a four-track grid reads as a caption on
 * that column rather than as the panel's answer.
 */
const searchPanelNoticeClasses = 'text-foreground-muted py-6 text-center text-sm';

export type SearchPanelNoticeProps = ComponentProps<'p'>;

/** The panel's one-line answer when there is nothing to list. */
export const SearchPanelNotice = ({ className, ...props }: SearchPanelNoticeProps) => (
  <p
    data-slot="search-panel-notice"
    className={clsxm(searchPanelNoticeClasses, className)}
    {...props}
  />
);

/**
 * The row at the foot of a column — "see everything".
 *
 * `mt-auto` is the row's own place at the end of its column rather than a gap
 * the column hands it, so a column holding fewer results still paints the row
 * against its bottom.
 */
const searchPanelActionsClasses = 'mt-auto flex justify-end pt-4';

export type SearchPanelActionsProps = ComponentProps<'div'>;

/** The row at the foot of a column. */
export const SearchPanelActions = ({ className, ...props }: SearchPanelActionsProps) => (
  <div
    data-slot="search-panel-actions"
    className={clsxm(searchPanelActionsClasses, className)}
    {...props}
  />
);

/**
 * The body of a search page opened with nothing to search for: a prompt, held
 * in the middle of a band of its own.
 *
 * A page body rather than a panel, and in this section because it is the same
 * thing one level out — what a shop says when there is nothing to list. The
 * heading inside it is the page's, composed from `@vs-shop/ui/heading`, because
 * a section never decides an outline level.
 */
const searchEmptyPromptClasses =
  'flex min-h-[40vh] flex-col items-center justify-center gap-4 py-25 text-center';

export type SearchEmptyPromptProps = ComponentProps<'div'>;

/** The centred prompt on a search page with no query. */
export const SearchEmptyPrompt = ({ className, ...props }: SearchEmptyPromptProps) => (
  <div
    data-slot="search-empty-prompt"
    className={clsxm(searchEmptyPromptClasses, className)}
    {...props}
  />
);

/** The line under that prompt's heading, saying where to type instead. */
const searchEmptyPromptTextClasses = 'text-foreground-muted';

export type SearchEmptyPromptTextProps = ComponentProps<'p'>;

/** The supporting line under the prompt. */
export const SearchEmptyPromptText = ({ className, ...props }: SearchEmptyPromptTextProps) => (
  <p
    data-slot="search-empty-prompt-text"
    className={clsxm(searchEmptyPromptTextClasses, className)}
    {...props}
  />
);

/**
 * The placeholder shapes.
 *
 * They mirror the panel's own layout rather than a spinner, so the popup keeps
 * its size while a query is in flight instead of collapsing to its close
 * button and jumping back open. Pure presentation — no hooks, so they render in
 * whichever environment imports them.
 */

/**
 * The placeholders' own recipes. They stand in for the real rows rather than
 * borrowing their classes: a suggestion's crop paints a ground and contains its
 * image, and a placeholder that inherited either would paint over the pulse
 * that is the whole point of it. The one recipe genuinely shared is the grid,
 * because the cells have to land where the real ones will.
 */
const searchPanelSkeletonLabelClasses = 'mb-3 h-4 w-28';
const searchPanelSkeletonStackClasses = 'flex flex-col gap-2';
const searchPanelSkeletonCellClasses = 'flex items-start gap-3 p-2';
const searchPanelSkeletonCellBodyClasses = 'flex min-w-0 flex-1 flex-col gap-2';
const searchPanelSkeletonRowClasses = 'h-4 w-full max-w-44';
const searchPanelSkeletonCropClasses = 'aspect-[4/3] w-20 shrink-0 rounded-md';
const searchPanelSkeletonFeatureCropClasses = 'aspect-[4/3] w-full max-w-[215px] rounded-md';
const searchPanelSkeletonCtaClasses = 'h-12 w-48 rounded-lg';
const searchPanelSkeletonFeatureLabelClasses = 'h-4 w-3/4';
const searchPanelSkeletonCellLineClasses = 'h-3 w-full';
const searchPanelSkeletonCellLineShortClasses = 'h-3 w-2/3';

export type SearchPanelSkeletonRowsProps = ComponentProps<typeof SearchSection> & {
  /** How many placeholder lines to stand in for. */
  readonly rows: number;
};

/** A placeholder group of text rows — brands, categories, articles. */
export const SearchPanelSkeletonRows = ({ rows, ...props }: SearchPanelSkeletonRowsProps) => (
  <SearchSection {...props}>
    <Skeleton className={searchPanelSkeletonLabelClasses} />
    <div data-slot="search-panel-skeleton-rows" className={searchPanelSkeletonStackClasses}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className={searchPanelSkeletonRowClasses} />
      ))}
    </div>
  </SearchSection>
);

export type SearchPanelSkeletonFeatureProps = ComponentProps<typeof SearchSection>;

/** A placeholder for the single featured product: its crop, then its name. */
export const SearchPanelSkeletonFeature = (props: SearchPanelSkeletonFeatureProps) => (
  <SearchSection {...props}>
    <Skeleton className={searchPanelSkeletonLabelClasses} />
    <div data-slot="search-panel-skeleton-feature" className={searchPanelSkeletonStackClasses}>
      <Skeleton className={searchPanelSkeletonFeatureCropClasses} />
      <Skeleton className={searchPanelSkeletonFeatureLabelClasses} />
    </div>
  </SearchSection>
);

export type SearchPanelSkeletonGridProps = ComponentProps<typeof SearchSection> & {
  /** How many placeholder cells to stand in for. */
  readonly cells: number;
};

/** A placeholder for the product grid, with the panel's own footer row under it. */
export const SearchPanelSkeletonGrid = ({
  className,
  cells,
  ...props
}: SearchPanelSkeletonGridProps) => (
  <SearchSection className={clsxm('flex flex-1 flex-col', className)} {...props}>
    <Skeleton className={searchPanelSkeletonLabelClasses} />
    <ul data-slot="search-panel-skeleton-grid" className={searchSuggestProductGridClasses}>
      {Array.from({ length: cells }, (_, index) => (
        <li
          key={index}
          data-slot="search-panel-skeleton-cell"
          className={searchPanelSkeletonCellClasses}
        >
          <Skeleton className={searchPanelSkeletonCropClasses} />
          <div
            data-slot="search-panel-skeleton-cell-body"
            className={searchPanelSkeletonCellBodyClasses}
          >
            <Skeleton className={searchPanelSkeletonCellLineClasses} />
            <Skeleton className={searchPanelSkeletonCellLineShortClasses} />
          </div>
        </li>
      ))}
    </ul>
    <SearchPanelActions>
      <Skeleton className={searchPanelSkeletonCtaClasses} />
    </SearchPanelActions>
  </SearchSection>
);
